import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { Donation, Donor } from '../types'
import md5 from 'md5'

interface DonorData {
  id: string // md5 hash of email
  name: string
  email: string
  total: number
  count: number
  country: string
  lastDonationDate: Date
  donations: Donation[]
  thankYouSent?: {
    date: string
    range: { start: string; end: string }
  }
  mergedFrom?: string[] // Track merged donor emails
}

interface DonationState {
  masterDonations: Donation[]  // Master set from file import
  masterDonors: Record<string, DonorData>  // Master set of donors
  filteredDonations: Donation[] // Filtered set based on date range
  filteredDonors: Record<string, DonorData> // Filtered set of donors
  currentSort: { column: string; order: string }
  filterStartDate: Date
  filterEndDate: Date
  filterCountry: string
}

interface DonationContextType {
  state: DonationState
  setDonations: (donations: Donation[]) => void
  setFilteredDonations: (donations: Donation[]) => void
  setCurrentSort: (sort: { column: string; order: string }) => void
  setSelectedDonor: (donor: Donor | null) => void
  setFilterStartDate: (date: Date) => void
  setFilterEndDate: (date: Date) => void
  filterDonations: () => void
  clearSavedData: () => void
  loadSavedData: () => void
  markThankYouSent: (donorEmail: string, donorName: string, dateRange: { start: string; end: string }) => void
  clearSentStatus: () => void
  setFilterCountry: (country: string) => void
}

const DonationContext = createContext<DonationContextType | undefined>(undefined)

const loadSavedState = () => {
  if (typeof window === 'undefined') {
    return {
      donations: [],
      donors: {},
      filterDates: {
        start: new Date(new Date().getFullYear(), 0, 1).toISOString(),
        end: new Date().toISOString()
      },
      filterCountry: ''
    }
  }

  const donations = localStorage.getItem('donations')
  const donors = localStorage.getItem('donors')
  const filterDates = localStorage.getItem('filterDates')
  
  return {
    donations: donations ? JSON.parse(donations) : [],
    donors: donors ? JSON.parse(donors) : {},
    filterDates: filterDates ? JSON.parse(filterDates) : {
      start: new Date(new Date().getFullYear(), 0, 1).toISOString(),
      end: new Date().toISOString()
    }
  }
}

export function DonationProvider({ children }: { children: ReactNode }) {
  const savedState = loadSavedState()
  
  const [state, setState] = useState<DonationState>({
    masterDonations: savedState.donations,
    masterDonors: savedState.donors,
    filteredDonations: [],
    filteredDonors: {},
    currentSort: { column: 'Created at', order: 'desc' },
    filterStartDate: new Date(savedState.filterDates.start),
    filterEndDate: new Date(savedState.filterDates.end),
    filterCountry: savedState.filterCountry || '',
  })

  const processNewDonations = (donations: Donation[], startDate?: Date, endDate?: Date) => {
    const donorMap: Record<string, DonorData> = { ...state.masterDonors }
    
    const filteredDonations = startDate && endDate 
      ? donations.filter(donation => {
          const donationDate = new Date(donation['Created at'])
          return donationDate >= startDate && donationDate <= endDate
        })
      : donations

    filteredDonations.forEach(donation => {
      const emailForId = typeof donation.Email === 'string' ? donation.Email : ''
      const nameForId = typeof donation.Name === 'string' ? donation.Name : ''
      const donorId = md5(emailForId.toLowerCase() + nameForId.toLowerCase())

      if (!donorMap[donorId]) {
        donorMap[donorId] = {
          id: donorId,
          name: nameForId,
          email: emailForId,
          total: 0,
          count: 0,
          country: typeof donation.Country === 'string' ? donation.Country : '',
          lastDonationDate: new Date(0),
          donations: [],
          thankYouSent: state.masterDonors[donorId]?.thankYouSent
        }
      }
      
      const donor = donorMap[donorId]
      donor.name = typeof donor.name === 'string' ? donor.name : ''
      donor.email = typeof donor.email === 'string' ? donor.email : ''
      
      donor.total += parseFloat(donation.Amount)
      donor.count += 1
      const donationDate = new Date(donation['Created at'])
      donor.lastDonationDate = new Date(Math.max(
        new Date(donor.lastDonationDate).getTime(),
        donationDate.getTime()
      ))
      donor.donations.push(donation)
    })

    return donorMap
  }

  const setDonations = (donations: Donation[]) => {
    console.log('setDonations called with:', donations.length, 'donations')
    
    // Process master donors from all donations
    const donors = processNewDonations(donations)
    console.log('processed donors:', Object.keys(donors).length)
    
    // Save master sets to localStorage and state
    localStorage.setItem('donations', JSON.stringify(donations))
    localStorage.setItem('donors', JSON.stringify(donors))
    
    setState(prev => ({ 
      ...prev, 
      masterDonations: donations,
      masterDonors: donors,
      // Also set filtered data initially
      filteredDonations: donations,
      filteredDonors: donors
    }))
    
    // Trigger filtering after state is updated
    setTimeout(() => filterDonations(donations, donors), 0)
  }

  const setFilterStartDate = (date: Date) => {
    setState(prev => ({ ...prev, filterStartDate: date }))
    const filterDates = {
      start: date.toISOString(),
      end: state.filterEndDate.toISOString()
    }
    localStorage.setItem('filterDates', JSON.stringify(filterDates))
  }

  const setFilterEndDate = (date: Date) => {
    setState(prev => ({ ...prev, filterEndDate: date }))
    const filterDates = {
      start: state.filterStartDate.toISOString(),
      end: date.toISOString()
    }
    localStorage.setItem('filterDates', JSON.stringify(filterDates))
  }

  const filterDonations = useCallback((masterDonations?: Donation[], masterDonors?: Record<string, DonorData>) => {
    setState(currentState => {
      const donations = Array.isArray(masterDonations) ? masterDonations : (currentState.masterDonations || [])
      const donors = masterDonors || currentState.masterDonors || {}
      
      const startDate = new Date(currentState.filterStartDate)
      startDate.setHours(0, 0, 0, 0)
      
      const endDate = new Date(currentState.filterEndDate)
      endDate.setHours(23, 59, 59, 999)
      
      // Adjust for timezone offset to prevent date rollover
      const tzOffset = endDate.getTimezoneOffset()
      if (tzOffset > 0) {
        endDate.setMinutes(endDate.getMinutes() - tzOffset)
      }
      
      console.log('Filtering with dates:', { startDate, endDate })
      
      // Filter donations
      const filteredDonations = donations.filter(donation => {
        const donationDate = new Date(donation['Created at'])
        const dateMatch = donationDate >= startDate && donationDate <= endDate
        const countryMatch = !currentState.filterCountry || 
          (typeof donation.Country === 'string' ? donation.Country : '') === currentState.filterCountry
        
        return dateMatch && countryMatch
      })

      console.log('Filtered donations:', {
        total: donations.length,
        filtered: filteredDonations.length,
        dateRange: `${startDate.toISOString()} - ${endDate.toISOString()}`
      })

      // Create filtered donors with only the filtered donations
      const filteredDonors: Record<string, DonorData> = {}
      filteredDonations.forEach(donation => {
        const emailForId = typeof donation.Email === 'string' ? donation.Email : ''
        const nameForId = typeof donation.Name === 'string' ? donation.Name : ''
        const donorId = md5(emailForId.toLowerCase() + nameForId.toLowerCase())

        if (!filteredDonors[donorId]) {
          const baseMasterDonor = donors[donorId]
          filteredDonors[donorId] = {
            ...(baseMasterDonor || {}), // Spread master donor if exists, or empty object
            id: baseMasterDonor?.id || donorId,
            name: nameForId,
            email: emailForId,
            total: 0,
            count: 0,
            country: typeof donation.Country === 'string' ? donation.Country : (baseMasterDonor?.country || ''),
            donations: [],
            lastDonationDate: new Date(0),
            thankYouSent: baseMasterDonor?.thankYouSent
          }
        }
        const donor = filteredDonors[donorId]
        donor.name = typeof donor.name === 'string' ? donor.name : ''
        donor.email = typeof donor.email === 'string' ? donor.email : ''

        donor.total += parseFloat(donation.Amount)
        donor.count += 1
        donor.donations.push(donation)
        const donationDate = new Date(donation['Created at'])
        donor.lastDonationDate = donationDate > donor.lastDonationDate ? donationDate : donor.lastDonationDate
      })
      
      return { ...currentState, filteredDonations, filteredDonors }
    })
  }, [])

  const loadSavedData = useCallback(() => {
    const savedDonations = localStorage.getItem('donations')
    const savedDonors = localStorage.getItem('donors')
    const savedFilterDates = localStorage.getItem('filterDates')
    const savedFilterCountry = localStorage.getItem('filterCountry')

    if (savedDonations) {
      const donations = JSON.parse(savedDonations)
      const donors = savedDonors ? JSON.parse(savedDonors) : {}
      const filterDates = savedFilterDates ? JSON.parse(savedFilterDates) : null
      
      setState(prev => ({
        ...prev,
        masterDonations: donations,
        masterDonors: donors,
        filterStartDate: filterDates ? new Date(filterDates.start) : prev.filterStartDate,
        filterEndDate: filterDates ? new Date(filterDates.end) : prev.filterEndDate,
        filterCountry: savedFilterCountry || '',
      }))

      setTimeout(() => filterDonations(donations, donors), 0)
    }
  }, [filterDonations])

  useEffect(() => {
    loadSavedData()
  }, [loadSavedData])

  const clearSavedData = () => {
    // Clear localStorage
    localStorage.removeItem('donations')
    localStorage.removeItem('donors')
    localStorage.removeItem('filterDates')
    
    // Reset state to initial values
    const initialState = {
      masterDonations: [],
      masterDonors: {},
      filteredDonations: [],
      filteredDonors: {},
      currentSort: { column: 'Created at', order: 'desc' },
      filterStartDate: new Date(new Date().getFullYear(), 0, 1),
      filterEndDate: new Date(),
      filterCountry: '',
    }
    
    setState(prev => ({
      ...prev,
      ...initialState
    }))
  }

  const markThankYouSent = useCallback((email: string, name: string, dateRange: { start: string; end: string }) => {
    console.log('Marking thank you sent for:', { email, name, dateRange })
    
    setState(prev => {
      const donorId = md5(email.toLowerCase() + name.toLowerCase())
      console.log('Donor ID:', donorId)
      console.log('Previous masterDonors:', prev.masterDonors)
      
      const newDonors = { ...prev.masterDonors }
      
      if (newDonors[donorId]) {
        newDonors[donorId] = {
          ...newDonors[donorId],
          thankYouSent: {
            date: new Date().toISOString(),
            range: dateRange
          }
        }
        
        console.log('Updated donor:', newDonors[donorId])
        localStorage.setItem('donors', JSON.stringify(newDonors))
        
        // Trigger a refilter to update the UI
        setTimeout(() => filterDonations(prev.masterDonations, newDonors), 0)
        
        return { ...prev, masterDonors: newDonors }
      }
      
      console.log('Donor not found:', donorId)
      return prev
    })
  }, [filterDonations])

  const clearSentStatus = useCallback(() => {
    setState(prev => {
      const newDonors = { ...prev.masterDonors }
      Object.keys(newDonors).forEach(id => {
        if (newDonors[id].thankYouSent) {
          newDonors[id] = {
            ...newDonors[id],
            thankYouSent: undefined
          }
        }
      })
      localStorage.setItem('donors', JSON.stringify(newDonors))
      return { ...prev, masterDonors: newDonors }
    })
    // Trigger refilter
    setTimeout(() => filterDonations(), 0)
  }, [filterDonations])

  const setFilterCountry = (country: string) => {
    setState(prev => ({ ...prev, filterCountry: country }))
    localStorage.setItem('filterCountry', country)
  }

  const value = {
    state: {
      ...state,
      donations: state.filteredDonations,  // For backward compatibility
      donors: state.filteredDonors         // For backward compatibility
    },
    setDonations,
    filterDonations,
    setFilteredDonations: (filteredDonations: Donation[]) => 
      setState(prev => ({ ...prev, filteredDonations })),
    setCurrentSort: (currentSort: { column: string; order: string }) => 
      setState(prev => ({ ...prev, currentSort })),
    setSelectedDonor: (selectedDonor: Donor | null) => 
      setState(prev => ({ ...prev, selectedDonor })),
    setFilterStartDate,
    setFilterEndDate,
    clearSavedData,
    loadSavedData,
    markThankYouSent,
    clearSentStatus,
    setFilterCountry,
  }

  return (
    <DonationContext.Provider value={value}>
      {children}
    </DonationContext.Provider>
  )
}

export function useDonation() {
  const context = useContext(DonationContext)
  if (context === undefined) {
    throw new Error('useDonation must be used within a DonationProvider')
  }
  return context
} 